import React, { SyntheticEvent, useRef, useState } from "react";
import { ValidationProblem, formErrors } from "../../api/backendApi";
import {
  MakeMappingModel,
  MakeMappingViewModel,
  makeMappingAPI,
} from "../../api/makeMappingAPI";
import { MakeModel } from "../../api/makeAPI";
import {
  Button,
  Modal,
  Form,
  Alert,
  ModalBody,
  ModalFooter,
  InputGroup,
} from "react-bootstrap";
import { PlusCircleFill, TrashFill } from "react-bootstrap-icons";
import { DataProviderModel } from "../../api/dataProviderApi";

interface MakeMappingsModalProps {
  mapping: MakeMappingViewModel;
  dataProviders: DataProviderModel[];
  crsMakes: MakeModel[];
  isCreate: boolean;
  onChange: (mapping: MakeMappingViewModel) => void;
  onCancel: () => void;
  modalText: string;
}

function MakeMappingsModal({
  mapping: initialMapping,
  dataProviders,
  crsMakes,
  isCreate,
  onChange,
  onCancel,
  modalText,
}: MakeMappingsModalProps) {
  const [mapping, setMapping] = useState(initialMapping);
  const [errors, setErrors] = useState<formErrors>({});

  const [selectedCrsMake, setSelectedCrsMake] = useState<string>();

  const selectDataProviderElement = useRef<HTMLSelectElement>(null);

  const initialDataProviderId = initialMapping.dataProviderId;
  const initialDataProviderMakeId = initialMapping.dataProviderMakeId;
  const initialDataProviderMakeDescription =
    initialMapping.dataProviderMakeDescription;

  const removeCrsMake = (index: number) => {
    let change = { crsMakes: mapping.crsMakes.filter((_, ii) => ii !== index) };

    let newMapping = { ...mapping, ...change };

    setMapping(newMapping);
  };

  const [show, setShow] = useState(true);

  const handleClose = () => {
    setShow(false);
    onCancel();
    setMapping(initialMapping);
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    // map from a view to a model
    let model: MakeMappingModel = {
      dataProviderId: mapping.dataProviderId,
      dataProviderMakeId: mapping.dataProviderMakeId,
      dataProviderMakeDescription: mapping.dataProviderMakeDescription,
      isSupported: mapping.isSupported,
      crsMakeCodes: mapping.isSupported
        ? mapping.crsMakes.map((make) => make.crsMakeCode ?? "")
        : [],
    };

    let call = isCreate
      ? makeMappingAPI.createMakeMapping(model)
      : makeMappingAPI.updateMakeMapping(
          initialDataProviderId,
          initialDataProviderMakeId,
          initialDataProviderMakeDescription,
          model,
        );

    call.then((result) => {
      if (result.ok) {
        mapping.dataProviderName =
          selectDataProviderElement.current &&
          selectDataProviderElement.current.selectedIndex >= 0
            ? selectDataProviderElement.current.options[
                selectDataProviderElement.current.selectedIndex
              ].text
            : "";

        if (!mapping.isSupported) mapping.crsMakes = [];

        onChange(mapping);
        handleClose();
      } else {
        let validation = result.problem as ValidationProblem;
        if (validation.validation) {
          setErrors(validation.validation);
        } else {
          let errors: formErrors = {};
          errors[""] = [`${result.problem.title} [${result.problem.instance}]`];
          setErrors(errors);
        }
      }
    });
  };

  const handleSelectedCrsMakeChanged = (e: any) => {
    setSelectedCrsMake(e.target.value);
  };

  const handleAddCrsMakeClicked = (e: SyntheticEvent) => {
    let selectedCrsMakeModel = crsMakes.find((m) => m.code === selectedCrsMake);

    if (!selectedCrsMakeModel) return;

    let change = {
      crsMakes: mapping.crsMakes.concat({
        crsMakeCode: selectedCrsMake,
        crsMakeDescription: selectedCrsMakeModel.name,
      }),
    };

    let newMapping = { ...mapping, ...change };
    setMapping(newMapping);

    // reset selected crs make
    setSelectedCrsMake(undefined);
  };

  const handleChange = (event: any) => {
    const { name, type, value, checked } = event.target;

    let updatedValue = value;

    if (type === "number") {
      updatedValue = updatedValue !== "" ? Number(updatedValue) : undefined;
    } else if (type === "checkbox") {
      updatedValue = checked;
    }

    const change = { [name]: updatedValue };
    const modified = { ...mapping, ...change };

    // ensure it's set as undefined when it's empty
    if (modified.dataProviderMakeDescription?.length === 0)
      modified.dataProviderMakeDescription = undefined;

    setMapping(modified);
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{modalText}</Modal.Title>
      </Modal.Header>
      <ModalBody>
        <Form onSubmit={handleSubmit} id="mappingForm">
          {errors[""] && (
            <div style={{ marginTop: "1rem" }}>
              {errors[""].map((errorMessage, index) => (
                <Alert key={errorMessage} variant="danger">
                  <p key={index}>{errorMessage}</p>
                </Alert>
              ))}
            </div>
          )}
          <div className="row">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="dataProviderId">Provider</Form.Label>
              <Form.Select
                id="dataProviderId"
                name="dataProviderId"
                value={mapping.dataProviderId}
                onChange={handleChange}
                ref={selectDataProviderElement}
                disabled={!isCreate}
              >
                {dataProviders.map((dataProvider) => (
                  <option value={dataProvider.id}>{dataProvider.name}</option>
                ))}
              </Form.Select>
              {errors.dataProviderId && (
                <div style={{ marginTop: "1rem" }}>
                  {errors.dataProviderId.map((e, index) => (
                    <Alert key={e} variant="danger">
                      <p>{e}</p>
                    </Alert>
                  ))}
                </div>
              )}
            </Form.Group>
          </div>
          <div className="row">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="dataProviderMakeId">
                Provider Make ID
              </Form.Label>
              <Form.Control
                name="dataProviderMakeId"
                id="dataProviderMakeId"
                type="number"
                placeholder={isCreate ? "Provider Make ID..." : ""}
                value={mapping.dataProviderMakeId ?? ""}
                onChange={handleChange}
                disabled={!isCreate}
              />
              {errors.dataProviderMakeId && (
                <div style={{ marginTop: "1rem" }}>
                  {errors.dataProviderMakeId.map((e, index) => (
                    <Alert key={e} variant="danger">
                      <p>{e}</p>
                    </Alert>
                  ))}
                </div>
              )}
            </Form.Group>
          </div>
          <div className="row">
            <Form.Group className="mb-3">
              <Form.Label htmlFor="dataProviderMakeDescription">
                Provider Make Description
              </Form.Label>
              <Form.Control
                name="dataProviderMakeDescription"
                id="dataProviderMakeDescription"
                type="text"
                placeholder={isCreate ? "Provider Make Description..." : ""}
                value={mapping.dataProviderMakeDescription ?? ""}
                onChange={handleChange}
                disabled={!isCreate}
              />
              {errors.dataProviderMakeDescription && (
                <div style={{ marginTop: "1rem" }}>
                  {errors.dataProviderMakeDescription.map((e, index) => (
                    <Alert key={e} variant="danger">
                      <p>{e}</p>
                    </Alert>
                  ))}
                </div>
              )}
            </Form.Group>
          </div>
          <div className="row">
            <Form.Group className="mb-3">
              {/* <Form.Label htmlFor="isSupported">Is Supported</Form.Label> */}
              <Form.Check
                name="isSupported"
                id="isSupported"
                type="switch"
                label="Available in CRS"
                checked={mapping.isSupported ?? false}
                onChange={handleChange}
              />
            </Form.Group>
          </div>
          {mapping.isSupported && (
            <div className="row">
              <Form.Group className="mb-3">
                <Form.Label htmlFor="">CRS Make Ids</Form.Label>
                {mapping.crsMakes.length === 0 ? (
                  <>
                    <span className="text-danger ms-2">Unmapped</span>
                    <br />
                  </>
                ) : (
                  <ul>
                    {mapping.crsMakes.map((x, index) => (
                      <li key={index}>
                        {x.crsMakeDescription ?? "-1"}
                        <TrashFill
                          className="text-danger"
                          width={20}
                          height={20}
                          onClick={() => removeCrsMake(index)}
                          style={{ marginLeft: "1rem" }}
                        ></TrashFill>
                      </li>
                    ))}
                  </ul>
                )}
                {errors.dataProviderMakeDescription && (
                  <div style={{ marginTop: "1rem" }}>
                    {errors.dataProviderMakeDescription.map((e, index) => (
                      <Alert key={e} variant="danger">
                        <p>{e}</p>
                      </Alert>
                    ))}
                  </div>
                )}
                {/* TODO: Replace with custom dropdown */}
                <InputGroup className="mb-3">
                  <Form.Select
                    id="toolbarMakes"
                    value={selectedCrsMake}
                    onChange={handleSelectedCrsMakeChanged}
                    size="sm"
                  >
                    {!selectedCrsMake && (
                      <option key="" value="">
                        [Select Make]
                      </option>
                    )}
                    {crsMakes
                      .filter(
                        (crsMake) =>
                          mapping.crsMakes.findIndex(
                            (x) => x.crsMakeCode === crsMake.code,
                          ) === -1,
                      )
                      .map((crsMake) => (
                        <option key={crsMake.code} value={crsMake.code}>
                          {crsMake.name}
                        </option>
                      ))}
                  </Form.Select>
                  <PlusCircleFill
                    className="text-primary"
                    onClick={handleAddCrsMakeClicked}
                    width={32}
                    height={32}
                    style={{
                      marginLeft: "1rem",
                    }}
                  ></PlusCircleFill>
                </InputGroup>
              </Form.Group>
            </div>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={() => onCancel()}>
          Cancel
        </Button>
        <Button variant="primary" form="mappingForm" type="submit">
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default MakeMappingsModal;
